import { type FC, useCallback, useEffect, useState } from 'react';

import { Button, Text, toast } from '@eluve/components';

import { type ReviewDecision, useMiniAppApi } from '../../hooks';
import type { MiniAppSurfaceProps } from '../../types';

import { EditFindingModal } from './EditFindingModal';
import { CLINICAL_FINDINGS_AGENT_ID } from './constants';
import { bumpFindings } from './findings-refresh';

interface PendingFinding {
  resourceType: string;
  id: string;
  label: string;
  display: string;
  detail: string | null;
  evidence: string | null;
  confidence: number | null;
}

type Verdict = 'approve' | 'reject';

/** Compact meta line: confidence · type · code. */
function metaLine(f: PendingFinding): string {
  return [
    f.confidence != null
      ? `${Math.round(f.confidence * 100)}% confident`
      : null,
    f.label,
    f.detail,
  ]
    .filter(Boolean)
    .join(' · ');
}

/**
 * Review surface (side-panel): lists this session's pending findings
 * (most- to least-confident), shows the supporting passage, and lets the
 * reviewer edit, approve, or reject each before submitting.
 */
export const FindingsReviewPanel: FC<MiniAppSurfaceProps> = ({
  context,
  onClose,
}) => {
  const { tenantId, appointmentId } = context;
  const api = useMiniAppApi(tenantId, CLINICAL_FINDINGS_AGENT_ID);

  const [findings, setFindings] = useState<PendingFinding[]>([]);
  const [verdicts, setVerdicts] = useState<Record<string, Verdict>>({});
  const [editing, setEditing] = useState<PendingFinding | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [rerunning, setRerunning] = useState(false);

  const load = useCallback(async () => {
    if (!appointmentId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await api.listFindings(appointmentId);
      if (res.status === 200) setFindings(res.body.findings);
    } finally {
      setLoading(false);
    }
  }, [api, appointmentId]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleVerdict = (id: string, v: Verdict) =>
    setVerdicts((prev) => {
      const next = { ...prev };
      if (next[id] === v) delete next[id];
      else next[id] = v;
      return next;
    });

  const submit = async () => {
    if (!appointmentId) return;
    const decisions: ReviewDecision[] = [];
    for (const f of findings) {
      const v = verdicts[f.id];
      if (v)
        decisions.push({ resourceType: f.resourceType, id: f.id, decision: v });
    }
    if (decisions.length === 0) {
      toast.warning('Approve or reject at least one finding first.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.review(appointmentId, decisions);
      if (res.status === 200) {
        toast.success(
          `${res.body.approved} approved, ${res.body.rejected} rejected.`,
        );
        setVerdicts({});
        bumpFindings();
        await load();
      } else {
        toast.error('Could not submit review.');
      }
    } catch {
      toast.error('Could not submit review.');
    } finally {
      setSubmitting(false);
    }
  };

  // Re-run the extraction for this session. The backend supersedes the
  // still-pending findings and proposes a fresh set, so any in-progress
  // verdicts (keyed by the old resource ids) are cleared before reloading.
  const rerun = async () => {
    if (!appointmentId) return;
    setRerunning(true);
    try {
      const res = await api.run({ appointmentId });
      if (res.status === 200) {
        const { outcome, factCount } = res.body;
        if (outcome === 'extracted') {
          toast.success(
            `Re-extracted ${factCount} finding${factCount === 1 ? '' : 's'}.`,
          );
        } else if (outcome === 'empty') {
          toast.warning('No new findings were found in this session.');
        } else {
          toast.warning('Nothing to extract for this session.');
        }
        setVerdicts({});
        bumpFindings();
        await load();
      } else {
        toast.error('Could not re-run the extraction.');
      }
    } catch {
      toast.error('Could not re-run the extraction.');
    } finally {
      setRerunning(false);
    }
  };

  if (loading) return <Text className="px-4 pt-2">Loading findings…</Text>;
  if (findings.length === 0) {
    return (
      <Text color="tertiary" className="px-4 pt-2">
        No findings awaiting review.
      </Text>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Scroll viewport runs edge-to-edge so its scrollbar pins to the panel
          edge; horizontal padding lives on the inner content so the rows clear
          the scrollbar and line up with the header. */}
      <div className="flex-1 overflow-y-auto">
        <div className="flex flex-col divide-y divide-border-secondary px-4 pt-2">
          {findings.map((f) => (
            <div key={f.id} className="flex flex-col gap-1 py-3">
              <div className="flex items-start justify-between gap-2">
                <Text weight="medium">{f.display}</Text>
                <Button
                  variant="ghost"
                  size="sm"
                  className="shrink-0"
                  onClick={() => setEditing(f)}
                >
                  Edit
                </Button>
              </div>
              <Text size="xs" color="tertiary">
                {metaLine(f)}
              </Text>
              {f.evidence ? (
                <Text
                  size="xs"
                  color="tertiary"
                  className="line-clamp-2 italic"
                >
                  “{f.evidence}”
                </Text>
              ) : null}
              <div className="flex gap-1 pt-1">
                <Button
                  size="sm"
                  variant={verdicts[f.id] === 'approve' ? 'default' : 'outline'}
                  onClick={() => toggleVerdict(f.id, 'approve')}
                >
                  Approve
                </Button>
                <Button
                  size="sm"
                  variant={
                    verdicts[f.id] === 'reject' ? 'destructive' : 'outline'
                  }
                  onClick={() => toggleVerdict(f.id, 'reject')}
                >
                  Reject
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="flex shrink-0 items-center justify-between gap-2 border-t border-border-secondary px-4 pt-3 pb-4">
        <Button
          variant="outline"
          size="sm"
          onClick={rerun}
          disabled={rerunning || submitting}
        >
          {rerunning ? 'Re-running…' : 'Re-run extraction'}
        </Button>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onClose}
            disabled={submitting || rerunning}
          >
            Close
          </Button>
          <Button size="sm" onClick={submit} disabled={submitting || rerunning}>
            {submitting ? 'Submitting…' : 'Submit review'}
          </Button>
        </div>
      </div>

      {editing ? (
        <EditFindingModal
          tenantId={tenantId}
          appointmentId={appointmentId ?? ''}
          finding={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load();
          }}
        />
      ) : null}
    </div>
  );
};
